import {useConfig} from '@aboutyou/config';
import {CaptchaEvent, Log, LoginCaptchaEvent} from '@aboutyou/mlm';
import {useScript} from '@aboutyou/ui-components';
import React, {useEffect, useRef} from 'react';
import styled from 'styled-components';

type TurnstileWidgetId = string;
interface WindowWithTurnstile extends Window {
  turnstile?: {
    render: (
      container: string,
      parameters?: Record<string, any>,
    ) => TurnstileWidgetId;
    reset: (container?: string) => void;
    remove: (widgetId: TurnstileWidgetId) => void;
  };
}

declare let window: WindowWithTurnstile;

interface CaptchaProps {
  onExpire?: () => void;
  onSuccess: (captchaCode: string) => void;
  reloadOnChange?: number | object; // if provided, if the value changes within the component lifecycle the captcha will reload
  className?: string;
  type?: 'default' | 'auth';
  uniqueKey: string;
}

/**
 * Cloudflare Turnstile API Docu => https://developers.cloudflare.com/turnstile/
 */
export const CloudflareTurnstile: React.FC<CaptchaProps> = ({
  onSuccess,
  onExpire,
  className,
  reloadOnChange,
  type = 'default',
  uniqueKey,
}) => {
  const captchaMainClassname = `ch-captcha-${uniqueKey}-${reloadOnChange}`;
  const config = useConfig();
  const loadScriptState = useScript(
    'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit',
    {isApiAvailable: () => typeof window.turnstile !== 'undefined'},
  );
  const turnstileWidgetId = useRef<TurnstileWidgetId>(undefined);

  // eslint-disable-next-line local-rules/too-complex-useeffects
  useEffect(() => {
    if (loadScriptState === 'success' && window.turnstile) {
      turnstileWidgetId.current = window.turnstile.render(
        `.${captchaMainClassname}`,
        {
          callback: (captchaCode: string) => {
            if (type === 'auth') {
              Log.compare({
                actionName: LoginCaptchaEvent,
                success: true,
                attributes: {},
              });
            } else {
              Log.compare({
                actionName: CaptchaEvent,
                success: true,
                attributes: {},
              });
            }

            onSuccess(captchaCode);
          },
          sitekey:
            type === 'default'
              ? config.cloudflareChallengeDefaultSiteKey
              : config.cloudflareChallengeSiteKey,
          'expired-callback': onExpire,
          // error codes: https://developers.cloudflare.com/turnstile/reference/client-side-errors/#error-codes
          'error-callback': (errorCode: string) => {
            if (type === 'auth') {
              Log.compare({
                actionName: LoginCaptchaEvent,
                success: false,
                attributes: {errorCode},
              });
            } else {
              Log.compare({
                actionName: CaptchaEvent,
                success: false,
                attributes: {errorCode},
              });
            }
            console.error(`>> cloudflare challenge error`, errorCode);
          },
        },
      );
      // eslint-disable-next-line local-rules/set-state-in-use-effect
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadScriptState, captchaMainClassname]);

  // reset when the selected variant is changed
  useEffect(() => {
    if (
      loadScriptState === 'success' &&
      window.turnstile &&
      typeof reloadOnChange === 'number'
    ) {
      onExpire?.();
    }
  }, [reloadOnChange, loadScriptState, onExpire]);

  useEffect(() => {
    return () => {
      if (window.turnstile && turnstileWidgetId.current) {
        window.turnstile.remove(turnstileWidgetId.current);
      }
    };
  }, []);

  return (
    <ChallengeApiContainer
      data-testid="captchaWidget"
      className={`${captchaMainClassname} ` + className}
    />
  );
};

export const Captcha = ({onSuccess, className, ...rest}: CaptchaProps) => {
  return (
    <CloudflareTurnstile
      onSuccess={onSuccess}
      className={className}
      {...rest}
    />
  );
};

const ChallengeApiContainer = styled.div`
  max-width: 375px;
`;
